import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import NProgress from 'nprogress';

import { authApi } from '@/utilities/axios';
import EventBus from '@/utilities/event-bus';
import { useAuthStore } from '@/modules/auth/store';
import { showToast } from '@/utilities/toast-handler';
import { formatDisplayDate } from '@/utilities/date-formatter';

const GROUP_URL = process.env.VUE_APP_API_VERSION + '/admin/permission/groups';
export const useGroupStore = defineStore('group', () => {
    const authStore = useAuthStore();
    const company_id = computed(() => {
        return authStore.getLoggedUserCompanyId;
    });
    // states
    const group = ref({
        company_id:null,
        code: null,
        description: null,
        active: null,
        permissions: [],
        allUsers:null
    });
    // states for edits
    const editGroupId = ref(null);
    const CompanyId = ref(company_id);
    const groupUserList = ref(null)
    const editPermissions = ref([]);

    const groups = ref([]);
    const params = ref({
        page_size: 10,
        page: 1,
    });
    const loading = ref(false);
    const usersLoading = ref(false);
    const totalRecords = ref(null);

    // getters
    const allGroups = computed(() => {
        return groups.value?.map((group) => {
            return {
                ...group,
                created_at: formatDisplayDate(group.created_at),
            };
        });
    });

    // actions
    // list out all the existing groups, currently we dont have company wise so it will list everything that exists in the db
    const initGroup = () => {
        group.value = {
            code: null,
            description: null,
            active: null,
            permissions: [],
            company_id:null,
            allUsers:null
        };
        editGroupId.value = null;
        editPermissions.value = null;
    };


    const getAllGroups = async (params, paginate = false, { silent = false } = {}) => {
        try {
            loading.value = true;
            const response = await authApi.get(GROUP_URL, {
                params: {
                    ...params,
                    company_id: company_id.value,
                    paginate: paginate,
                },
                silent,
            });
            if (response.statusCode === 200) {
                // Depending upon paginated response, the data is located at separate part
                groups.value = paginate ? response.data?.data : response.data;
                totalRecords.value = paginate ? response.data?.total : response.data?.length;
            }
        } catch (error) {
            if (!silent) {
                showToast({}, error?.data?.message ?? 'Failed to fetch all groups');
            }
        } finally {
            loading.value = false;
        }
    };

    // fetches the users that exists in the group
    const fetchGroupUsers = async (groupId) => {
        try {
            groupUserList.value = [];
            usersLoading.value = true;
            const response = await authApi.get(`${GROUP_URL}/${groupId}/users`, {
                params: {
                    company_id: company_id.value,
                },
            });
            if (response.statusCode === 200) {
                groupUserList.value = response.data;
                return response.data;
            }
        } catch (error) {
            showToast({}, error?.data?.message ?? 'Failed to fetch group users');
        } finally {
            usersLoading.value = false;
        }
    };

    const fetchUsersForDropdown = async (groupId = null) => {

        try {
            const response =  await authApi.get(`${GROUP_URL}/${groupId}/users`, {
                params: {
                    company_id: company_id.value,
                    count:true,
                },
            });
            group.value.allUsers = response.data.allUser ?? false
        } catch (error) {
            console.error('Failed to fetch users for dropdown', error);
        }
    };


    // create  a new group
    const createGroup = async (body) => {
        try {
            NProgress.start();
            EventBus.emit('show-operation-loader');
            const response = await authApi.post(GROUP_URL, body);
            if (response.statusCode === 201) {
                showToast(response.toast, 'New Group has been created');
                groups.value.push(response.data);
                initGroup();
                return true;
            }
        } catch (error) {
            showToast({}, 'Failed to create group');
        } finally {
            NProgress.done();
            EventBus.emit('hide-operation-loader');
        }
    };

    // edit a group, setup edit instance for a group

    const editGroup = async (groupId) => {
        try {
            const existingGroup = groups.value.find((group) => group.id === groupId);
            editGroupId.value = groupId;
            editPermissions.value = existingGroup?.permissions;
            group.value = {
                code: existingGroup?.code,
                description: existingGroup?.description,
                active: existingGroup?.active === 1 ? true : false,
                permissions: [],
                users: existingGroup?.users?.map(user => ({
                    id: user.id,
                    name: user.name.charAt(0).toUpperCase() + user.name.slice(1) +
                        ' ' +
                        (user.surname ? user.surname.charAt(0).toUpperCase() + user.surname.slice(1) : ''),
                })) || [], // Ensures an empty array if no users exist,
            };
        } catch (error) {
            showToast({}, 'Error while editing a group');
        }
    };

    // update an existing group
    const updateGroup = async (groupId, body) => {
        try {
            NProgress.start();
            EventBus.emit('show-operation-loader');
            const response = await authApi.patch(`${GROUP_URL}/${groupId}`, body);
            if (response.statusCode === 200) {
                showToast(response.toast, 'Group has been updated');
                initGroup();
                return true;
            }
        } catch (error) {
            showToast({}, 'Failed to update group');
        } finally {
            NProgress.done();
            EventBus.emit('hide-operation-loader');
        }
    };

    const deleteGroup = async (groupId) => {
        try {
            NProgress.start();
            EventBus.emit('show-operation-loader');
            const response = await authApi.delete(`${GROUP_URL}/${groupId}`);
            if (response.statusCode === 200) {
                showToast(response.toast, 'Group has been deleted successfully');
                return true;
            }
        } catch (error) {
            showToast({}, error?.data?.message ?? 'Failed to delete the group');
        } finally {
            NProgress.done();
            EventBus.emit('hide-operation-loader');
        }
    };


    return {
        // helper states
        params,
        loading,
        usersLoading,
        totalRecords,
        // states
        editGroupId,
        editPermissions,
        group,
        groups,
        // getters
        allGroups,
        // actions
        initGroup,
        getAllGroups,
        fetchGroupUsers,
        createGroup,
        editGroup,
        CompanyId,
        updateGroup,
        deleteGroup,
        fetchUsersForDropdown
    };
});
